/**
 * 1:1 duplicate of the helpers from
 * `Comments/CommentReplyForm/utils/replyFormUtils.ts` that the
 * mention reply form needs. Per "duplicate first, abstract later" —
 * both copies fold into one shared util during Phase 4.
 */

import type { FragmentOf } from '~publish/graphql'

import type { SavedReplyItem_SavedReply } from '../../../components/SavedRepliesPopover/SavedReplyItem'

export const SUBMIT_REPLY_KEYBOARD_SHORTCUT = 'Enter'

/**
 * Whether a keyboard event should submit the reply form — `Cmd`/`Ctrl` +
 * `Enter` while expanded. Mirrors Comments' `shouldSubmitOnKeyPress`.
 */
export const shouldSubmitOnKeyPress = (
  event: KeyboardEvent,
  isExpanded: boolean,
): boolean => {
  if (!isExpanded) return false
  const isCmdOrCtrl = event.metaKey || event.ctrlKey
  return isCmdOrCtrl && event.key === SUBMIT_REPLY_KEYBOARD_SHORTCUT
}

/**
 * Inserts new text into existing text with proper spacing.
 *
 * @param currentText - The current text content
 * @param newText - The text to insert
 * @returns The combined text with proper spacing
 *
 * @example
 * ```tsx
 * insertText("Hello", "world") // "Hello world"
 * insertText("", "Hello") // "Hello"
 * insertText("Hello ", "world") // "Hello world"
 * ```
 */
export const insertText = (currentText: string, newText: string): string => {
  const trimmedCurrent = currentText.trim()
  return trimmedCurrent ? `${trimmedCurrent} ${newText}` : newText
}

/**
 * Extracts text content from a saved reply.
 *
 * @param savedReply - The saved reply object
 * @returns The text content or empty string if not available
 *
 * @example
 * ```tsx
 * const text = extractSavedReplyText(savedReply) // "Thank you for your feedback!"
 * ```
 */
export const extractSavedReplyText = (
  savedReply: Pick<FragmentOf<typeof SavedReplyItem_SavedReply>, 'content'>,
): string => {
  if (!savedReply) return ''
  return savedReply?.content?.text ?? ''
}

/**
 * Trims and validates reply text for submission.
 *
 * @param text - The raw text input
 * @returns The trimmed text if valid, or null if invalid
 *
 * @example
 * ```tsx
 * validateAndTrimReplyText("  Hello world  ") // "Hello world"
 * validateAndTrimReplyText("   ") // null
 * ```
 */
export const validateAndTrimReplyText = (text: string): string | null => {
  const trimmed = text.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Formats a username with the @ prefix if not already present.
 *
 * @param username - The username to format
 * @returns The formatted username with @ prefix
 *
 * @example
 * ```tsx
 * formatMentionUsername("testuser") // "@testuser"
 * formatMentionUsername("@testuser") // "@testuser"
 * ```
 */
export const formatMentionUsername = (username: string): string =>
  username.startsWith('@') ? username : `@${username}`
